///
/// EarthBrowser
///
/// Created by Matt Giger on 1/2/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Clock } from "@/earthbrowser/Clock"
import { type Database } from "@/earthbrowser/database/Database"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { geographic } from "@/earthbrowser/utils/geographic"
import { engineBridge } from "@/earthbrowser/engineBridge"
import type { LocationNearbyInfo } from "@/redux/userInterfaceSlice"
import type { Feature, Point } from "@shared-types/geojson"
import type { AdminInfo, CountryInfo, LocationInfo } from "@shared-types/location-info"
import type { RenderActionSet } from "@stovokor/engine/Renderer"
import { RenderState } from "@stovokor/engine/RenderState"
import type { FocusTarget } from "@stovokor/gestures/FocusTarget"
import { trig } from "@stovokor/math/math"
import { computeViewportBBox } from "../viewport-bbox"
import { dot, normal, type vec3 } from "@stovokor/math/vector"
import type { AuroraDatasetLayer } from "../aurora/AuroraDatasetLayer"
import { BuoyObservationsTable } from "../buoys/BuoyObservationsTable"
import { BuoyStationsTable } from "../buoys/BuoyStationsTable"
import { DatasetLayer, type DatasetManifest, type DatasetSceneObject, type DatasetSyncConfig } from "../DatasetLayer"
// Import the concrete module, not the ../weather-grid barrel: the barrel
// re-exports React hooks, which would drag React into the embed bundle
import { getWeatherGridService } from "../weather-grid/WeatherGridService"
import type { CurrentWeather } from "../weather-grid/WeatherCompute"
import { WebcamStationsTable } from "../webcams/WebcamStationsTable"
import { CITY_DISTANCE_THRESHOLDS, CITY_RANK_DEFAULT, type CityImportance, type CityProperties } from "./city-types"
import { CityLabelNode } from "./CityLabelNode"
import { CityLabelsOverlay, type CityLabelData, type CityWeatherInfo, type LabelMotion } from "./CityLabelsOverlay"
import { CountriesTable } from "./CountriesTable"
import { CountryAdmin2Table, type Admin2Row } from "./CountryAdmin2Table"
import { CountryAdminTable } from "./CountryAdminTable"
import { LocationsTable, type CityQueryRow } from "./LocationsTable"
import { type WorldViewport } from "@stovokor/engine/WorldViewport"

/**
 * Feature type for location scene objects (cities, places)
 * Can be used for place name overlays in the future
 */
export interface LocationFeature {
	id: string
	geoid: number
	name: string
	country: string
	admin1: string
	admin2: string
	tz: string
	pop: number
	lon: number
	lat: number
	alt: number
	code: string
}

/** Raw Arrow row shape for the cities feed (only fields we read) */
interface CityRow {
	geonameid: unknown
	lon: unknown
	lat: unknown
	pop: unknown
	alt: unknown
	name: string | null | undefined
	country: string | null | undefined
	admin1: string | null | undefined
	admin2: string | null | undefined
	tz: string | null | undefined
	code: string | null | undefined
}

/**
 * Maximum number of city scene objects to keep in memory
 * This prevents performance issues at high altitudes
 * Cities are ordered by population, so the limit keeps the most important ones
 */
const MAX_CITY_SCENE_OBJECTS = 500

/**
 * Camera altitude below which city labels show wind arrows (regional zoom)
 */
const WIND_ARROW_MAX_ALTITUDE_KM = 3000

/**
 * Scene-clock movement (ms) that triggers a weather re-sample, so label
 * temps/conditions follow the time slider. Grid timesteps are ~3-hourly and
 * linearly interpolated, so 15 minutes gives visibly smooth scrub updates
 * without re-decoding every frame.
 */
const WEATHER_CLOCK_RESAMPLE_MS = 15 * 60_000

/**
 * Current conditions plus cross-dataset context for the location hover callout
 */
export interface CityHoverContext {
	weather: CurrentWeather | null
	nearby: LocationNearbyInfo
}

/**
 * Fast equirectangular distance approximation (km) - fine at nearby-station
 * scales (< a few hundred km)
 */
function approxDistanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
	const dLat = lat2 - lat1
	let dLon = Math.abs(lon2 - lon1)
	if (dLon > 180) dLon = 360 - dLon
	const x = dLon * Math.cos(((lat1 + lat2) / 2) * Math.PI / 180)
	return Math.sqrt(x * x + dLat * dLat) * 111.32
}

/**
 * LocationsDatasetLayer - Manages location lookup tables and city labels
 *
 * This layer handles multiple related datasets:
 * - countries: Country information for lookups
 * - admin1: First-level administrative divisions (states, provinces)
 * - admin2: Second-level administrative divisions (counties, districts)
 * - cities: City/place name data for search and labels
 *
 * Also provides distance-based city label placement using 2D overlay labels
 * for crisp rendering at all distances.
 *
 * City selection is driven by a spatial-dominance rank computed client-side
 * after ingest (see LocationsTable.computeAndStoreRanks): each city's rank is
 * the coarsest level at which it meets a population floor and has no larger
 * city within that level's suppression radius. Queries filter and order by
 * rank, which guarantees every visible region shows its locally dominant
 * cities before any region gets secondary ones - regardless of how
 * city-proper populations compare across regions.
 */
/**
 * Construction options for contexts that shouldn't pay for the full
 * locations stack (the embed): a compact cities checkpoint, optional
 * sub-datasets, and an off switch for the weather-grid download.
 */
export interface LocationsLayerOptions {
	/**
	 * Which cities checkpoint to sync. "cities" is the full ~280k-row set;
	 * "cities_embed" is the relevance-selected top-N single shard published
	 * by tools/geonames (rank/capital/population ordering). Ranks are
	 * recomputed client-side over whatever subset was ingested.
	 */
	citiesDatasetId?: "cities" | "cities_embed"
	/** Sync admin2 (county names, ~2 MB) — only used for reverse-geo subtitles */
	includeAdmin2?: boolean
	/**
	 * Allow the current-weather grid download (weather_current.bin.gz) that
	 * feeds label chips and hover conditions. Off in embeds unless the
	 * city_weather layer was explicitly requested.
	 */
	weatherEnabled?: boolean
}

export class LocationsDatasetLayer extends DatasetLayer<LocationFeature> {
	// Which sub-datasets sync (embed skips admin2 and swaps the cities id)
	private readonly subDatasets: readonly string[]
	private readonly citiesDatasetId: "cities" | "cities_embed"
	private readonly weatherEnabled: boolean

	// Viewport query throttling
	private lastViewportQueryAt = 0
	private lastViewportQueryKey = ''

	// 2D label overlay for UI rendering
	private labelsOverlay: CityLabelsOverlay
	private world: WorldViewport
	private overlayInitialized = false

	// City scene objects (CityLabelNode instances)
	private citySceneObjects = new Map<string, CityLabelNode>()

	// In-memory weather cache populated asynchronously from WeatherGridService
	private weatherCache = new Map<number, CurrentWeather>()
	// Geoids the grid returned no data for - skipped by fill passes so they
	// aren't refetched every 2s, retried by the periodic full pass
	private weatherMisses = new Set<number>()
	private weatherCacheTimestamp = 0       // Last completed pass of any kind (real time)
	private lastFullWeatherRefresh = 0      // Last completed full (all-cities) pass (real time)
	private weatherCacheClockMs = 0         // Scene-clock time the cache was sampled at
	private weatherRefreshInFlight = false
	private weatherCacheDirty = false

	// Aurora probability per geoid, sampled from the aurora hex grid (60s TTL)
	private auroraCache = new Map<number, { pct: number | null, t: number }>()

	// Nearby-station indexes for the city hover callout (webcams, buoys).
	// Loaded lazily on first hover, refreshed every 10 minutes.
	private webcamIndex: Array<{ lat: number, lon: number, name: string }> | null = null
	private buoyIndex: Array<{ lat: number, lon: number, name: string, waterTempC: number | null }> | null = null
	private nearbyIndexLoadedAt = 0
	private nearbyIndexLoading = false

	// Wind arrows on labels are only useful at regional zoom
	private lastShowWind = false

	// Position memory - tracks which offset index each city was last placed at
	// This prevents labels from jumping around as other cities come/go
	private labelPositionMemory = new Map<string, number>()

	// Position memory persistence - track when each city was last visible
	// Keeps memory for a few seconds after removal so labels reappear in same spot
	private labelLastSeenTime = new Map<string, number>()
	private readonly POSITION_MEMORY_PERSIST_MS = 3000  // Keep memory for 3 seconds

	// Extra collision allowance for a label keeping its established offset:
	// a displayed label's position is immutable (it never relocates to make
	// room), so it tolerates transient grazing overlaps rather than fading
	// over them. When genuinely blocked it fades out instead of moving.
	private readonly INCUMBENT_MAX_CONFLICT_PX2 = 450

	// Movement detection - only drives fade/hysteresis timing in the overlay.
	// Zoom (radius change) is detected separately from panning because the
	// overlay uses cause-aware timing: long gates while panning, short gates
	// while zooming so the label set tracks altitude promptly.
	private lastCameraPositionX = 0
	private lastCameraPositionY = 0
	private lastCameraPositionZ = 0
	private lastCameraRadius = 0
	private hasPreviousCameraState = false
	private isActivelyMoving = false
	private isActivelyZooming = false
	private moveStabilizeTime = 0
	private zoomStabilizeTime = 0
	private readonly MOVE_STABILIZE_MS = 300
	private readonly ZOOM_STABILIZE_MS = 250
	// Per-frame radius change (km) that counts as zooming rather than noise
	private readonly ZOOM_RADIUS_DELTA_KM = 0.005

	// Continuous placement cadence (MapLibre-style): the full declutter pass
	// runs at this interval whether or not the camera is moving, and labels
	// are only reprojected on the frames in between. Stability comes from the
	// persistence score bonus, remembered offsets, and overlay fades - not
	// from freezing the label set during motion.
	private nextPlacementTime = 0
	private readonly PLACEMENT_INTERVAL_MS = 150

	// City ids selected by the most recent placement pass. Reprojection
	// frames use this so they never resurrect labels the pass faded out.
	private lastPlacedIds = new Set<string>()

	// Query generation counter to prevent stale async results from overwriting fresh ones
	private queryGeneration = 0

	constructor(context: EarthBrowserContext, world: WorldViewport, options?: LocationsLayerOptions) {
		super(context, "locations")

		this.world = world
		this.citiesDatasetId = options?.citiesDatasetId ?? "cities"
		this.weatherEnabled = options?.weatherEnabled ?? true
		this.subDatasets = [
			"countries",
			"admin1",
			...(options?.includeAdmin2 ?? true ? ["admin2"] : []),
			this.citiesDatasetId,
		]

		// Create the 2D labels overlay
		this.labelsOverlay = new CityLabelsOverlay(context, this)
	}

	// Fraction of camera-to-city distance used to lift the label depth
	// toward the camera, so exaggerated terrain under the city can't
	// z-fight the label (same value as the map and satellite label
	// overlays). Small enough that 3D entities in front of a city - storm
	// columns, cutouts, satellite models - still occlude its label. The
	// limb needs no lift: grazing-angle labels fade out (see
	// GRAZING_HIDE_COS) before the globe's steep depth gradient there
	// could slice a label quad.
	private static readonly LABEL_DEPTH_LIFT_FRAC = 0.02

	// Grazing-angle fade, on the cosine of the incidence angle between the
	// city's surface normal and the direction to the camera (1 looking
	// straight down, 0 at the mathematical horizon). Labels dissolve as the
	// ground tilts edge-on, well before the horizon, so nothing piles up
	// along the limb or floats over the sky, and a city dropping behind the
	// curve while panning has already faded instead of lingering through
	// the hide hysteresis.
	private static readonly GRAZING_HIDE_COS = 0.06   // ~86.5° incidence: hidden
	private static readonly GRAZING_FULL_COS = 0.15   // ~81.4° incidence: fully opaque

	/** Cosine of the view incidence angle at a city (see GRAZING_HIDE_COS) */
	private static cityIncidence(cameraPos: vec3, cityPos: vec3): number {
		return dot(normal(cityPos), normal(cameraPos.subtract(cityPos)))
	}

	/**
	 * Altitude opacity for a tier: full within [near, far], smooth decay to
	 * 0.3 beyond far (no discontinuity at the far threshold)
	 */
	private static altitudeAlpha(cameraAltitudeKm: number, importance: CityImportance): number {
		const far = CITY_DISTANCE_THRESHOLDS[importance].far
		if (cameraAltitudeKm <= far) return 1.0
		const beyondRatio = (cameraAltitudeKm - far) / far
		return Math.max(0.3, 1.0 - beyondRatio * 0.7)
	}

	/** Smoothstep opacity for a view incidence: 0 at or past GRAZING_HIDE_COS */
	private static grazingAlpha(cosIncidence: number): number {
		const t = (cosIncidence - LocationsDatasetLayer.GRAZING_HIDE_COS)
			/ (LocationsDatasetLayer.GRAZING_FULL_COS - LocationsDatasetLayer.GRAZING_HIDE_COS)
		if (t <= 0) return 0
		if (t >= 1) return 1
		return t * t * (3 - 2 * t)
	}

	// Label placement offsets from city screen position (shared between declutter and position-only update)
	private static readonly POSITION_OFFSETS: Array<[number, number]> = [
		[0, -25],     // Above (preferred)
		[0, 25],      // Below
		[30, 0],      // Right
		[-30, 0],     // Left
		[20, -20],    // Upper-right
		[-20, -20],   // Upper-left
		[20, 20],     // Lower-right
		[-20, 20],    // Lower-left
	]

	private static readonly LABEL_SIZE_BY_IMPORTANCE: Record<CityImportance, { w: number, h: number }> = {
		capital: { w: 150, h: 46 },
		major: { w: 130, h: 42 },
		medium: { w: 105, h: 38 },
		small: { w: 85, h: 32 },
		town: { w: 70, h: 26 }
	}

	// Extra empty space reserved around each label's collision footprint.
	// Larger values force more separation between labels, lowering overall
	// label density without changing the rendered label sizes.
	private static readonly LABEL_SPACING_PADDING_PX = 10

	/**
	 * Current camera motion state for the overlay's cause-aware timing
	 */
	private get cameraMotion(): LabelMotion {
		if (this.isActivelyZooming) return 'zooming'
		if (this.isActivelyMoving) return 'panning'
		return 'idle'
	}

	/**
	 * Screen position for a label that is not being (re)placed this pass:
	 * city position plus its remembered offset, matching where the label was
	 * last drawn. Fading labels must hold this position - snapping them to
	 * the bare city point while they are still visible reads as a jump.
	 */
	private rememberedLabelPosition(cityId: string, importance: CityImportance, cityX: number, cityY: number): { x: number, y: number } {
		const posIndex = this.labelPositionMemory.get(cityId)
		const [dx, dy] = posIndex !== undefined && posIndex < LocationsDatasetLayer.POSITION_OFFSETS.length
			? LocationsDatasetLayer.POSITION_OFFSETS[posIndex]
			: LocationsDatasetLayer.POSITION_OFFSETS[0]
		const size = LocationsDatasetLayer.LABEL_SIZE_BY_IMPORTANCE[importance]
		return { x: cityX + dx, y: cityY + dy + size.h / 2 }
	}

	private cityWorldDepth(state: RenderState, city: CityLabelNode): number {
		const pos = city.cartesianPosition
		const toCameraVec = state.camera.position.subtract(pos)
		const dist = toCameraVec.length
		const toCamera = normal(toCameraVec)
		const liftKm = dist * LocationsDatasetLayer.LABEL_DEPTH_LIFT_FRAC
		const liftedPos = pos.add(toCamera.multiply(liftKm))
		return state.camera.worldToScreenDepth(liftedPos)
	}

	/**
	 * Refresh weather cache for city scene objects from the grid service.
	 *
	 * Three kinds of passes:
	 * - Fill pass (2s after new cities appear): fetches only cities missing
	 *   from the cache, so newly loaded cities get weather promptly.
	 * - Full pass (every 5 minutes): refetches every city to keep values fresh.
	 * - Clock pass (scene clock moved >= WEATHER_CLOCK_RESAMPLE_MS from the
	 *   cached sample time, at most 1/s): refetches every city so temps and
	 *   conditions follow the time slider, not just the day/night icons.
	 *
	 * All passes sample the grid at the scene clock time (Clock.time), so a
	 * scrubbed or animating clock shows interpolated past/future conditions.
	 * getCurrentWeather decodes from the already-loaded in-memory grid (no
	 * per-city network requests), so covering all scene objects is cheap -
	 * never cap or subset the list, or some labels permanently miss weather.
	 */
	private refreshWeatherCache(): void {
		// Weather disabled (embed without city_weather): never fetch the
		// ~24 MB current-weather grid just to render name labels
		if (!this.weatherEnabled) return
		if (this.weatherRefreshInFlight) return

		const now = Date.now()
		const clockMs = Clock.time.date.getTime()
		const fullPassDue = now - this.lastFullWeatherRefresh >= 5 * 60_000
		const clockPassDue = Math.abs(clockMs - this.weatherCacheClockMs) >= WEATHER_CLOCK_RESAMPLE_MS &&
			now - this.weatherCacheTimestamp >= 1_000
		const fillPassDue = this.weatherCacheDirty && now - this.weatherCacheTimestamp >= 2_000
		const allCitiesPass = fullPassDue || clockPassDue
		if (!allCitiesPass && !fillPassDue) return

		const service = getWeatherGridService()
		if (!service.isCurrentGridLoaded) {
			service.ensureCurrentGrid().catch(() => {})
			return
		}

		const cities = [...this.citySceneObjects.values()].filter(n => {
			const geoid = n.feature.properties.geonameid
			if (!geoid) return false
			if (allCitiesPass) return true
			return !this.weatherCache.has(geoid) && !this.weatherMisses.has(geoid)
		})

		this.weatherRefreshInFlight = true
		// Clear before the pass starts: cities created while it runs re-set
		// the flag and are picked up by the next fill pass
		this.weatherCacheDirty = false

		const clockSec = clockMs / 1000
		Promise.all(
			cities.map(async (city) => {
				try {
					const geom = city.feature.geometry
					if (geom?.type !== 'Point') return
					const [lon, lat] = geom.coordinates
					const geoid = city.feature.properties.geonameid
					const weather = await service.getCurrentWeather(lat, lon, clockSec)
					if (weather) {
						this.weatherCache.set(geoid, weather)
						this.weatherMisses.delete(geoid)
					} else {
						this.weatherMisses.add(geoid)
					}
				} catch {}
			})
		).then(() => {
			this.weatherCacheTimestamp = Date.now()
			if (fullPassDue) {
				this.lastFullWeatherRefresh = Date.now()
			}
			if (allCitiesPass) {
				// Only all-cities passes may claim the cache is aligned to the
				// clock - a fill pass leaves existing entries at the old time
				this.weatherCacheClockMs = clockMs
			}
			this.weatherRefreshInFlight = false
		}).catch(() => {
			this.weatherRefreshInFlight = false
		})
	}

	// ==================== Weather / Night / Aurora / Nearby ====================

	/**
	 * Night test against the scene's live sun direction (the same vector that
	 * shades the globe), with a small twilight margin so icons flip to their
	 * moon variants a touch past the terminator rather than exactly on it
	 */
	private isNightAtPosition(position: vec3): boolean {
		return dot(normal(position), RenderState.lightDirection) < -0.05
	}

	/**
	 * Aurora probability (%) for a city tonight, sampled from the aurora hex
	 * grid. Cached per geoid for 60s. Returns null below a 15% floor so the
	 * badge only appears when there is a real chance.
	 */
	private auroraChanceAt(geoid: number, latDeg: number, lonDeg: number): number | null {
		// The aurora oval never reaches low latitudes - skip the lookup
		if (Math.abs(latDeg) < 40) return null

		const now = Date.now()
		const cached = this.auroraCache.get(geoid)
		if (cached && now - cached.t < 60_000) return cached.pct

		const manager = engineBridge.getState()?.datasetScene.layerManager
		const auroraLayer = manager?.getLayer("aurora") as AuroraDatasetLayer | undefined
		const pct = auroraLayer?.sampleProbabilityAt(latDeg, lonDeg) ?? null
		const result = pct !== null && pct >= 15 ? Math.round(pct) : null
		this.auroraCache.set(geoid, { pct: result, t: now })
		return result
	}

	/**
	 * Assemble the label-facing weather info for a city from the grid cache
	 * plus scene-derived context (night side, aurora chance, wind LOD)
	 */
	private buildWeatherInfo(sceneObject: CityLabelNode, showWind: boolean): CityWeatherInfo | undefined {
		// Weather chips on labels toggled off from the Layers panel
		if (!this.datasetVisible("city_weather")) return undefined

		const geoid = sceneObject.feature.properties.geonameid
		if (!geoid) return undefined
		const weather = this.weatherCache.get(geoid)
		if (!weather) return undefined

		const geo = sceneObject.geographicPosition
		const latDeg = geo.latitude * (180 / Math.PI)
		const lonDeg = geo.longitude * (180 / Math.PI)
		const isNight = this.isNightAtPosition(sceneObject.cartesianPosition)

		return {
			temp_c: weather.temp_c,
			weather_code: weather.weather_code,
			weather_desc: weather.weather_desc,
			cloud_cover_pct: weather.cloud_cover_pct,
			feels_like_c: weather.feels_like_c,
			wind_speed_mps: weather.wind_speed_mps,
			wind_dir_deg: weather.wind_dir_deg,
			pressure_trend_3h_hpa: weather.pressure_trend_3h_hpa,
			is_night: isNight,
			aurora_chance_pct: isNight ? this.auroraChanceAt(geoid, latDeg, lonDeg) : null,
			show_wind: showWind
		}
	}

	/**
	 * Kick off (or refresh) the webcam/buoy indexes used for nearby lookups.
	 * Fire-and-forget: hover callouts read whatever is loaded so far.
	 */
	private ensureNearbyIndexes(): void {
		const now = Date.now()
		if (this.nearbyIndexLoading || now - this.nearbyIndexLoadedAt < 10 * 60_000) return
		this.nearbyIndexLoading = true

		void (async () => {
			try {
				const [webcams, buoyStations, buoyObs] = await Promise.all([
					WebcamStationsTable.getAll(),
					BuoyStationsTable.getAll(),
					BuoyObservationsTable.getLatestByStation()
				])
				this.webcamIndex = webcams
					.filter(w => w.status === "active")
					.map(w => ({ lat: w.lat, lon: w.lon, name: w.name }))
				this.buoyIndex = buoyStations.map(s => ({
					lat: s.lat,
					lon: s.lon,
					name: s.name,
					waterTempC: buoyObs.get(s.station_id)?.water_temp ?? null
				}))
				this.nearbyIndexLoadedAt = Date.now()
			} catch {
				// Tables may not be synced yet - retried on the next hover
			} finally {
				this.nearbyIndexLoading = false
			}
		})()
	}

	/**
	 * Everything the location hover callout needs beyond the base city info:
	 * current conditions plus cross-dataset context (nearby webcams, coastal
	 * buoy water temperature, aurora chance). Synchronous - reads caches only.
	 */
	public getCityHoverContext(geoid: number, latDeg: number, lonDeg: number): CityHoverContext {
		this.ensureNearbyIndexes()

		const weather = this.weatherCache.get(geoid) ?? null

		let webcamCount = 0
		let nearestWebcamName: string | null = null
		let nearestWebcamKm: number | null = null
		if (this.webcamIndex) {
			for (const cam of this.webcamIndex) {
				const d = approxDistanceKm(latDeg, lonDeg, cam.lat, cam.lon)
				if (d <= 30) {
					webcamCount++
					if (nearestWebcamKm === null || d < nearestWebcamKm) {
						nearestWebcamKm = d
						nearestWebcamName = cam.name
					}
				}
			}
		}

		let buoyName: string | null = null
		let buoyWaterTempC: number | null = null
		let buoyKm: number | null = null
		if (this.buoyIndex) {
			for (const buoy of this.buoyIndex) {
				if (buoy.waterTempC === null) continue
				const d = approxDistanceKm(latDeg, lonDeg, buoy.lat, buoy.lon)
				if (d <= 100 && (buoyKm === null || d < buoyKm)) {
					buoyKm = d
					buoyName = buoy.name
					buoyWaterTempC = buoy.waterTempC
				}
			}
		}

		const cartesian = new geographic(lonDeg * trig.rad, latDeg * trig.rad, 0).cartesian
		const isNight = this.isNightAtPosition(cartesian)

		return {
			weather,
			nearby: {
				webcamCount,
				nearestWebcamName,
				nearestWebcamKm,
				buoyName,
				buoyWaterTempC,
				buoyKm,
				auroraChancePct: isNight ? this.auroraChanceAt(geoid, latDeg, lonDeg) : null,
				isNight
			}
		}
	}

	// ==================== Sync Configuration ====================

	/**
	 * Return sync config. The datasetId is "cities" as the primary,
	 * but we handle multiple datasets in applyCheckpoint.
	 */
	public getSyncConfig(): DatasetSyncConfig {
		return {
			datasetId: this.citiesDatasetId, // Primary dataset ID for version tracking
			pollIntervalMs: 0, // No polling - location data rarely changes
			needsSync: true,
			supportsDelta: false,
			// ~25 MB / ~330k rows on a fresh install; let the toggled layers
			// (radar, clouds, ...) sync first so they paint within seconds
			deferInitialSync: true,
		}
	}

	/**
	 * Return all dataset IDs that this layer manages
	 */
	public getSubDatasetIds(): readonly string[] {
		return this.subDatasets
	}

	public async createTable(db: Database): Promise<void> {
		await LocationsTable.create(db)
		await CountriesTable.create(db)
		await CountryAdminTable.create(db)
		await CountryAdmin2Table.create(db)
	}

	public override async getStoredRowCount(datasetId: string): Promise<number | null> {
		switch (datasetId) {
			case "cities":
			case "cities_embed": return LocationsTable.getCount()
			case "countries": return CountriesTable.getCount()
			case "admin1": return CountryAdminTable.getCount()
			case "admin2": return CountryAdmin2Table.getCount()
			default: return null
		}
	}

	/**
	 * Called after a checkpoint/delta is applied. Compute spatial-dominance
	 * label ranks for the freshly ingested cities.
	 */
	public override async onSyncComplete(): Promise<void> {
		await LocationsTable.computeAndStoreRanks()
		this.lastViewportQueryKey = ''  // Force viewport requery with fresh ranks
	}

	// Lazy rank computation for installs that synced cities before ranks existed
	private ranksEnsured = false

	/**
	 * Ensure label ranks exist for an already-populated table (existing
	 * installs won't re-sync, so onSyncComplete never fires for them).
	 * Fresh installs are handled by onSyncComplete after ingest.
	 */
	private async ensureRanksComputed(): Promise<void> {
		if (this.ranksEnsured) return
		this.ranksEnsured = true
		try {
			const count = await LocationsTable.getCount()
			if (count === 0) return  // Empty table - onSyncComplete handles the fresh-sync path
			if (await LocationsTable.ranksComputed()) return

			await LocationsTable.computeAndStoreRanks()
			this.lastViewportQueryKey = ''  // Force viewport requery with fresh ranks
		} catch (error) {
			console.error('[LocationsDatasetLayer] Failed to compute label ranks:', error)
			this.ranksEnsured = false  // Retry on next attempt
		}
	}

	/**
	 * Apply checkpoint for cities dataset.
	 * Note: countries, admin1, admin2 are synced via syncSubDatasets.
	 */
	public async applyCheckpoint(
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<unknown[]>
	): Promise<void> {
		// Cities can have multiple shards
		for (const shard of manifest.checkpoint.shards) {
			const url = this.resolveUrl(shard.url, shard.path)
			// console.log(`[LocationsDatasetLayer] Downloading cities shard: ${url}`)
			const rows = await downloadArrowRows(url)
			console.log(`[LocationsDatasetLayer] Parsed ${rows.length} city rows`)

			const locations = this.parseCityRows(rows)
			await LocationsTable.insert(locations)
		}
	}

	/**
	 * Apply checkpoint for a specific sub-dataset (countries, admin1, admin2)
	 */
	public async applySubDatasetCheckpoint(
		datasetId: string,
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<unknown[]>
	): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return

		const url = this.resolveUrl(shard.url, shard.path)
		console.log(`[LocationsDatasetLayer] Downloading ${datasetId} shard: ${url}`)
		const rows = await downloadArrowRows(url)
		// console.log(`[LocationsDatasetLayer] Parsed ${rows.length} ${datasetId} rows`)

		switch (datasetId) {
			case "countries":
				await CountriesTable.insert(rows as CountryInfo[])
				break

			case "admin1":
				const admins = (rows as AdminInfo[]).map(a => ({
					...a,
					admin_code: this.adminCodeForCountryAdmin(a.admin_code),
				}))
				await CountryAdminTable.insert(admins)
				break

			case "admin2":
				const parsed: Admin2Row[] = []
				for (const r of rows as Array<{ admin_code: string, country_code: string, name: string }>) {
					const parts = r.admin_code.split(".")
					if (parts.length < 3) continue
					parsed.push({
						country_code: r.country_code,
						admin1_code: parts[1],
						admin2_code: parts.slice(2).join("."),
						name: r.name,
					})
				}
				await CountryAdmin2Table.insert(parsed)
				break
		}
	}

	private adminCodeForCountryAdmin(admin_code: string): string {
		// Publisher uses GeoNames "full codes" like "US.CA" for admin1
		// Client schema expects admin_code without country prefix (e.g. "CA")
		const parts = admin_code.split(".")
		return parts.length >= 2 ? parts[1] : admin_code
	}

	private parseCityRows(rows: unknown[]): LocationInfo[] {
		const locations: LocationInfo[] = []
		for (const r of rows as CityRow[]) {
			const geoid = Number(r.geonameid)
			const lon = Number(r.lon)
			const lat = Number(r.lat)

			// geoid is the primary key and lon/lat are required (the city can't be
			// placed or rendered without them). Drop rows missing any of these
			// rather than letting NaN/NULL reach the table.
			if (!Number.isFinite(geoid) || !Number.isFinite(lon) || !Number.isFinite(lat)) continue

			const pop = Number(r.pop ?? 0)
			const alt = Number(r.alt ?? 0)

			locations.push({
				geoid,
				name: r.name != null ? String(r.name) : "",
				country: r.country != null ? String(r.country) : "",
				admin1: r.admin1 ? String(r.admin1) : "",
				admin2: r.admin2 ? String(r.admin2) : "",
				tz: r.tz ? String(r.tz) : "",
				pop: Number.isFinite(pop) ? pop : 0,
				lon,
				lat,
				alt: Number.isFinite(alt) ? alt : 0,
				code: r.code ? String(r.code) : "",
			})
		}
		return locations
	}

	// ==================== City Feature Access ====================

	/**
	 * Get a city feature by ID
	 */
	public getFeatureById(id: string): Feature<Point, CityProperties> | undefined {
		const sceneObject = this.citySceneObjects.get(id)
		if (!sceneObject) return undefined
		return sceneObject.feature as Feature<Point, CityProperties>
	}

	/**
	 * Get a CityLabelNode by ID
	 */
	public getCityNodeById(id: string): CityLabelNode | undefined {
		return this.citySceneObjects.get(id)
	}

	/**
	 * Override base class getSceneObject to look in citySceneObjects
	 * Cities are indexed by geoid (e.g., "5128581" for New York)
	 * CityLabelNode implements the required requestFocus method for focus handling
	 */
	override getSceneObject(id: string): DatasetSceneObject<LocationFeature> | undefined {
		return this.citySceneObjects.get(id) as DatasetSceneObject<LocationFeature> | undefined
	}

	/**
	 * Load a city scene object on-demand by geoid.
	 * Used when a user searches for a city that isn't currently in the viewport.
	 * Queries the database and creates the CityLabelNode if found.
	 */
	override async loadSceneObjectById(id: string): Promise<DatasetSceneObject<LocationFeature> | undefined> {
		// Check if already loaded
		const existing = this.citySceneObjects.get(id)
		if (existing) {
			return existing as unknown as DatasetSceneObject<LocationFeature>
		}

		// Query the city from the database
		const geoid = parseInt(id, 10)
		if (isNaN(geoid)) {
			console.warn(`[LocationsDatasetLayer] Invalid geoid: ${id}`)
			return undefined
		}

		const cityData = await LocationsTable.queryByGeoid(geoid)
		if (!cityData) {
			console.warn(`[LocationsDatasetLayer] City not found: ${id}`)
			return undefined
		}

		// Convert to GeoJSON Feature format
		const feature = this.rowsToFeatures([cityData])[0]
		if (!feature || !this.filterCityFeature(feature)) {
			return undefined
		}

		// Create the scene object and add it to the layer
		const node = new CityLabelNode(this.context, feature)
		this.citySceneObjects.set(id, node)
		this.world.addNode(node)

		console.log(`[LocationsDatasetLayer] Loaded city on-demand: ${cityData.name} (${id})`)

		return node as unknown as DatasetSceneObject<LocationFeature>
	}

	// ==================== Scene Objects ====================

	/**
	 * Query location features for scene rendering.
	 * Can be used for place name overlays in the future.
	 */
	public async queryFeatures(_db: Database): Promise<LocationFeature[]> {
		// For now, return empty - no scene objects yet
		// In the future, this could query visible cities for label overlays
		return []
	}

	public createSceneObject(_feature: LocationFeature): DatasetSceneObject<LocationFeature> | undefined {
		// No scene objects yet - can be implemented for place name overlays
		return undefined
	}

	/**
	 * Update city scene objects from GeoJSON features
	 * Features are expected to be ordered by population DESC
	 */
	public updateCitySceneObjects(features: Feature<Point, CityProperties>[]): void {
		// Limit the features to process
		const limitedFeatures = features.slice(0, MAX_CITY_SCENE_OBJECTS)

		const existingIds = new Set(this.citySceneObjects.keys())
		const newIds = new Set(limitedFeatures.map(f => String(f.id)))

		// Remove scene objects for features that no longer exist
		for (const id of existingIds) {
			if (!newIds.has(id)) {
				this.removeCitySceneObject(id)
			}
		}

		// Create or update scene objects
		for (const feature of limitedFeatures) {
			const id = String(feature.id)
			const existing = this.citySceneObjects.get(id)

			if (existing) {
				existing.updateFeature(feature)
			} else if (this.filterCityFeature(feature)) {
				const node = new CityLabelNode(this.context, feature)
				this.citySceneObjects.set(id, node)
				this.world.addNode(node)
				if (!this.weatherCache.has(feature.properties.geonameid)) {
					this.weatherCacheDirty = true
				}
			}
		}

		// If we still have too many scene objects, prune the excess
		if (this.citySceneObjects.size > MAX_CITY_SCENE_OBJECTS) {
			this.pruneExcessCitySceneObjects()
		}
	}

	/**
	 * Filter features to only include valid city points
	 */
	private filterCityFeature(feature: Feature<Point, CityProperties>): boolean {
		// Only accept Point geometries
		if (feature.geometry?.type !== 'Point') {
			return false
		}

		// Ensure required properties exist
		if (!feature.properties.geonameid || !feature.properties.name) {
			return false
		}

		// Ensure importance tier is valid
		const validImportance: CityImportance[] = ['capital', 'major', 'medium', 'small', 'town']
		if (!validImportance.includes(feature.properties.importance)) {
			return false
		}

		return true
	}

	/**
	 * Remove excess scene objects, keeping the most regionally dominant cities
	 * (lowest rank first, population as tiebreaker)
	 */
	private pruneExcessCitySceneObjects(): void {
		const entries: Array<{ id: string, rank: number, population: number }> = []
		for (const [id, obj] of this.citySceneObjects) {
			entries.push({ id, rank: obj.rank, population: obj.population })
		}

		// Sort by rank ascending, then population descending
		entries.sort((a, b) => a.rank !== b.rank ? a.rank - b.rank : b.population - a.population)

		// Remove entries beyond the limit
		const toRemove = entries.slice(MAX_CITY_SCENE_OBJECTS)
		for (const entry of toRemove) {
			this.removeCitySceneObject(entry.id)
		}
	}

	/**
	 * Remove a city scene object by ID
	 */
	private removeCitySceneObject(id: string): void {
		const sceneObject = this.citySceneObjects.get(id)
		if (!sceneObject) return

		sceneObject.destroy()
		this.citySceneObjects.delete(id)
	}

	// ==================== Rendering ====================

	/**
	 * Initialize the overlay by adding it to the UIViewport
	 * Called on first render when selectionCoordinator is available
	 */
	private initializeOverlay(): void {
		if (this.overlayInitialized) return

		// Add overlay to label viewport via SelectionCoordinator
		// The label viewport preserves the world depth buffer so labels
		// are occluded by 3D scene objects (ISS, satellites, etc.)
		this.labelsOverlay.depth = 0
		this.context.selectionCoordinator.addLabelNode(this.labelsOverlay)

		this.overlayInitialized = true

		// Compute label ranks if this install synced cities before ranks existed
		void this.ensureRanksComputed()
	}

	override render(state: RenderState): RenderActionSet[] {
		// Initialize overlay on first render
		this.initializeOverlay()

		// City labels toggled off from the Layers panel: clear the overlay
		// and skip viewport/weather work (city lights are unaffected)
		if (!this.datasetVisible("location")) {
			this.labelsOverlay.updateLabels([])
			return []
		}

		this.refreshWeatherCache()
		this.updateViewportQuery(state)
		this.updateDeclutter(state)

		// Don't call super.render() - we don't want 3D rendering of city nodes
		// The overlay handles all label rendering
		return []
	}

	/**
	 * Whether city weather may be shown at all: the grid download is
	 * allowed in this context and the city weather layer is on. Gates the
	 * label chips and the focused-city weather vignette alike.
	 */
	public get cityWeatherAllowed(): boolean {
		return this.weatherEnabled && this.datasetVisible("city_weather")
	}

	/**
	 * Read dataset visibility from the store (checked per frame; cheap)
	 */
	private datasetVisible(type: "location" | "city_weather"): boolean {
		return engineBridge.getState()?.earthbrowser.visibleDatasets.includes(type) ?? true
	}

	private updateViewportQuery(state: RenderState): void {
		// Update bbox query for cities based on current view frustum (throttled)
		const now = performance.now()
		if (now - this.lastViewportQueryAt < 300) return
		this.lastViewportQueryAt = now

		const cameraPos = state.camera.position
		const cameraAltitudeKm = new geographic(cameraPos).altitude

		// Global mode at high altitude: most of a hemisphere is visible, so a
		// bbox query buys nothing over querying top-ranked cities globally
		if (cameraAltitudeKm > 5000) {
			const key = `global|${Math.round(cameraAltitudeKm / 100) * 100}`
			if (key === this.lastViewportQueryKey) return
			this.lastViewportQueryKey = key

			// Query top cities globally - scale limit and max rank by altitude
			const { limit, maxRank } = this.getQueryParamsForAltitude(cameraAltitudeKm)
			void this.queryAndUpdateCities(undefined, limit, maxRank)
			return
		}

		// Null only when the globe is entirely off-screen — nothing to label
		const bbox = computeViewportBBox(state, 200)
		if (!bbox) return

		const rounded = bbox.map(v => Math.round(v * 10) / 10)
		const key = `bbox|${rounded.join(',')}|${Math.round(cameraAltitudeKm / 50) * 50}`
		if (key === this.lastViewportQueryKey) return
		this.lastViewportQueryKey = key

		// Query cities within the bounding box using R*Tree
		const { limit, maxRank } = this.getQueryParamsForAltitude(cameraAltitudeKm)
		void this.queryAndUpdateCities(bbox, limit, maxRank)
	}

	/**
	 * Get query parameters (limit, maxRank) based on camera altitude.
	 *
	 * Rank is the density control: lower maxRank = only regionally dominant
	 * cities. Because queries order by rank first, the limit is a safety cap
	 * that trims the least-dominant tail without sacrificing geographic
	 * coverage (unlike the old population ordering, where the limit cut
	 * entire low-density regions).
	 */
	private getQueryParamsForAltitude(altitudeKm: number): { limit: number, maxRank: number } {
		// Close up - show everything including small towns
		if (altitudeKm < 600) return { limit: 1500, maxRank: CITY_RANK_DEFAULT }

		// Mid-range - regional views
		if (altitudeKm < 1500) return { limit: 1200, maxRank: 7 }
		if (altitudeKm < 3000) return { limit: 1000, maxRank: 6 }

		// High altitude / global mode - only regionally dominant cities.
		// Rank <= 5 is bounded (~2700 cells worldwide, far fewer on land),
		// so results are inherently well distributed across the hemisphere.
		if (altitudeKm < 6000) return { limit: 800, maxRank: 5 }
		return { limit: 600, maxRank: 4 }
	}

	/**
	 * Query cities from database and update scene objects
	 *
	 * @param bbox - Bounding box [minLon, minLat, maxLon, maxLat] or undefined for global query
	 * @param limit - Maximum number of cities to fetch
	 * @param maxRank - Maximum spatial-dominance rank to include
	 */
	private async queryAndUpdateCities(
		bbox: [number, number, number, number] | undefined,
		limit: number,
		maxRank: number
	): Promise<void> {
		const gen = ++this.queryGeneration
		try {
			let rows: CityQueryRow[]

			if (bbox) {
				const [minLon, minLat, maxLon, maxLat] = bbox
				rows = await LocationsTable.queryByBBox(minLon, minLat, maxLon, maxLat, limit, maxRank)
			} else {
				rows = await LocationsTable.queryTopCities(limit, maxRank)
			}

			// Discard results from stale queries that were superseded during flight
			if (gen !== this.queryGeneration) {
				return
			}

			const features = this.rowsToFeatures(rows)
			this.updateCitySceneObjects(features)
		} catch (error) {
			console.error('[LocationsDatasetLayer] Failed to query cities:', error)
		}
	}

	/**
	 * Convert database query rows to GeoJSON Feature format for CityLabelNode
	 */
	private rowsToFeatures(rows: CityQueryRow[]): Feature<Point, CityProperties>[] {
		return rows.map(row => ({
			type: 'Feature' as const,
			id: String(row.geoid),
			geometry: {
				type: 'Point' as const,
				coordinates: [row.lon, row.lat, row.alt ?? 0]
			},
			properties: {
				geonameid: row.geoid,
				name: row.name,
				country: row.country,
				admin1: row.admin1,
				admin2: row.admin2,
				// Note: property must be named `pop` - CityProperties and
				// CityLabelNode.population read `pop`, not `population`
				pop: row.pop,
				alt: row.alt ?? 0,
				tz: row.tz,
				importance: row.importance,
				rank: row.rank
			}
		}))
	}


	private updateDeclutter(state: RenderState): void {
		// Google-Earth-like city labeling:
		// - Use camera altitude to enable importance tiers
		// - Score candidates by importance, population, and view alignment
		// - Enforce screen-space declutter via occupancy grid

		const now = performance.now()
		const focusedNode = this.context.focusManager.getFocusedNode()
		const cameraPos = state.camera.position
		const cameraGeo = new geographic(cameraPos)
		const cameraAltitudeKm = cameraGeo.altitude
		const cameraRadius = cameraPos.length

		// Wind arrows only appear at regional zoom where they are legible
		this.lastShowWind = cameraAltitudeKm < WIND_ARROW_MAX_ALTITUDE_KM

		// === MOVEMENT DETECTION ===
		// Simple position comparison - any camera position change means
		// movement; a radius change specifically means zooming
		if (this.hasPreviousCameraState) {
			const dx = cameraPos.x - this.lastCameraPositionX
			const dy = cameraPos.y - this.lastCameraPositionY
			const dz = cameraPos.z - this.lastCameraPositionZ
			const distSq = dx * dx + dy * dy + dz * dz
			if (distSq > 1e-6) {
				this.isActivelyMoving = true
				this.moveStabilizeTime = now + this.MOVE_STABILIZE_MS
			} else if (now > this.moveStabilizeTime) {
				this.isActivelyMoving = false
			}

			const dr = Math.abs(cameraRadius - this.lastCameraRadius)
			if (dr > this.ZOOM_RADIUS_DELTA_KM) {
				this.isActivelyZooming = true
				this.zoomStabilizeTime = now + this.ZOOM_STABILIZE_MS
			} else if (now > this.zoomStabilizeTime) {
				this.isActivelyZooming = false
			}
		}

		this.hasPreviousCameraState = true
		this.lastCameraPositionX = cameraPos.x
		this.lastCameraPositionY = cameraPos.y
		this.lastCameraPositionZ = cameraPos.z
		this.lastCameraRadius = cameraRadius

		// Between placement passes, only reproject the current label set.
		// The full pass below runs on a fixed cadence even during motion, so
		// a rapid zoom out re-thins labels within one interval instead of
		// piling the close-up set until the camera settles.
		if (now < this.nextPlacementTime) {
			this.updatePositionsOnly(state, focusedNode)
			return
		}
		this.nextPlacementTime = now + this.PLACEMENT_INTERVAL_MS

		// Hide all city labels above 12,000 KM altitude
		if (cameraAltitudeKm > 12000) {
			this.lastPlacedIds.clear()
			this.labelsOverlay.updateLabels([], this.cameraMotion)
			return
		}

		const marginPx = 200

		const width = state.width
		const height = state.height

		const candidates: Array<{
			node: CityLabelNode
			x: number
			y: number
			score: number
			alpha: number
			worldDepth: number
		}> = []

		const maxPopForScore = 40_000_000
		const logMaxPop = Math.log10(maxPopForScore + 1)

		for (const [, sceneObject] of this.citySceneObjects) {
			const importance = sceneObject.importanceTier
			const pop = sceneObject.population
			const pos = sceneObject.cartesianPosition

			// Ground must face the camera by more than a grazing angle. This
			// subsumes the horizon test (incidence <= 0 past the horizon) and
			// drops the band of edge-on cities just inside it
			const grazing = LocationsDatasetLayer.grazingAlpha(LocationsDatasetLayer.cityIncidence(cameraPos, pos))
			if (grazing <= 0) {
				continue
			}

			// Must be generally in front of the camera
			const toCity = normal(pos.subtract(cameraPos))
			const viewAlign = Math.max(0, dot(toCity, state.camera.state.forward))
			if (viewAlign <= 0.005) {
				continue
			}

			const screen = state.camera.worldToScreen(pos)
			if (!Number.isFinite(screen.x) || !Number.isFinite(screen.y)) {
				continue
			}

			if (
				screen.x < -marginPx ||
				screen.y < -marginPx ||
				screen.x > width + marginPx ||
				screen.y > height + marginPx
			) {
				continue
			}

			const alpha = LocationsDatasetLayer.altitudeAlpha(cameraAltitudeKm, importance) * grazing
			if (alpha < 0.05) continue

			// Deterministic priority (the Been-Yap "consistency" principle,
			// same model as Mapbox symbol placement): the score is a pure
			// function of the city itself - regional dominance rank first,
			// population as tie-break. Deliberately NO view-dependent terms
			// and NO incumbency bonus: for any camera pose the same cities
			// win in the same order, so panning and rotating cannot reshuffle
			// winners, and the label set only changes where the geometry
			// actually forces it (fades cover those moments).
			const popScore = Math.log10(Math.max(0, pop) + 1) / logMaxPop
			const score =
				(CITY_RANK_DEFAULT - sceneObject.rank) * 2.0 +
				popScore

			candidates.push({
				node: sceneObject,
				x: screen.x,
				y: screen.y,
				score,
				alpha,
				worldDepth: this.cityWorldDepth(state, sceneObject)
			})
		}

		// Target pool size - candidates to consider for placement
		// Need enough to find good placements, but not so many that it's slow
		const targetPoolSize = (() => {
			if (cameraAltitudeKm < 50) return 400
			if (cameraAltitudeKm < 150) return 500
			if (cameraAltitudeKm < 300) return 600
			if (cameraAltitudeKm < 600) return 700
			if (cameraAltitudeKm < 1500) return 800
			if (cameraAltitudeKm < 3000) return 900
			if (cameraAltitudeKm < 5000) return 800
			return 600
		})()

		// Sort by score and take top candidates
		candidates.sort((a, b) => b.score - a.score)
		const pooled = candidates.slice(0, targetPoolSize)

		// Even if no new candidates, still update positions for fading labels
		if (pooled.length === 0) {
			this.lastPlacedIds.clear()
			const fadingLabels: CityLabelData[] = []
			const activeCityIds = this.labelsOverlay.getActiveCityIds()
			for (const cityId of activeCityIds) {
				const sceneObject = this.citySceneObjects.get(cityId)
				if (!sceneObject) continue
				if (sceneObject === focusedNode) continue

				const pos = sceneObject.cartesianPosition
				const screen = state.camera.worldToScreen(pos)
				if (!Number.isFinite(screen.x) || !Number.isFinite(screen.y)) continue

				const labelPos = this.rememberedLabelPosition(cityId, sceneObject.importanceTier, screen.x, screen.y)
				fadingLabels.push({
					id: cityId,
					name: sceneObject.name,
					importance: sceneObject.importanceTier,
					screenX: labelPos.x,
					screenY: labelPos.y,
					cityScreenX: screen.x,
					cityScreenY: screen.y,
					// 0 once the city is edge-on/behind the limb: the overlay
					// skips the hide hysteresis for that geometric hide
					alpha: LocationsDatasetLayer.grazingAlpha(LocationsDatasetLayer.cityIncidence(cameraPos, pos)),
					worldDepth: this.cityWorldDepth(state, sceneObject),
					shouldShow: false,
					weather: this.buildWeatherInfo(sceneObject, this.lastShowWind)
				})
			}
			this.labelsOverlay.updateLabels(fadingLabels, this.cameraMotion)
			return
		}

		// ============ CARTOGRAPHIC MULTI-POSITION LABEL PLACEMENT ============
		// For each label, try multiple positions around the point and pick the best one
		// Uses AABB collision detection with placed labels

		const visibleLabels: CityLabelData[] = []
		const visibleIds = new Set<string>()

		// Max visible labels - collision detection will naturally limit this
		// These are upper bounds, actual placement depends on available space
		const maxLabels = (() => {
			if (cameraAltitudeKm < 50) return 55
			if (cameraAltitudeKm < 150) return 70
			if (cameraAltitudeKm < 300) return 85
			if (cameraAltitudeKm < 600) return 100
			if (cameraAltitudeKm < 1500) return 120
			if (cameraAltitudeKm < 3000) return 140
			if (cameraAltitudeKm < 5000) return 150
			if (cameraAltitudeKm < 8000) return 140
			return 120
		})()

		// Label bounding boxes for collision detection
		// Each box: { x1, y1, x2, y2 } where (x1,y1) is top-left
		interface LabelBox {
			x1: number
			y1: number
			x2: number
			y2: number
		}
		const placedBoxes: LabelBox[] = []

		// Label bounding boxes for collision detection
		// These should roughly match the actual rendered label sizes (two lines with weather)
		const labelSizeByImportance = LocationsDatasetLayer.LABEL_SIZE_BY_IMPORTANCE

		// 8 candidate positions for each label (offsets from city point)
		// Tighter offsets - labels should stay close to the city point
		// Values are [dx, dy] offsets for label center from city point
		const positionOffsets = LocationsDatasetLayer.POSITION_OFFSETS

		// Calculate overlap area (for scoring)
		const overlapArea = (a: LabelBox, b: LabelBox): number => {
			const xOverlap = Math.max(0, Math.min(a.x2, b.x2) - Math.max(a.x1, b.x1))
			const yOverlap = Math.max(0, Math.min(a.y2, b.y2) - Math.max(a.y1, b.y1))
			return xOverlap * yOverlap
		}

		// Check if box is within screen bounds
		const isOnScreen = (box: LabelBox): boolean => {
			return box.x1 >= -20 && box.x2 <= width + 20 && box.y1 >= -20 && box.y2 <= height + 20
		}

		// Current on-screen footprints of labels that were visible last pass
		// and have not yet been placed this pass, keyed by city id. New labels
		// treat these as space to avoid (soft conflict), so they slot into
		// gaps instead of landing on a label the viewer is already reading
		// and forcing it away. A footprint is removed once its owner is
		// placed (it becomes a hard placed box instead).
		const incumbentBoxes = new Map<string, LabelBox>()
		for (const c of pooled) {
			const fid = String(c.node.feature.id)
			if (!this.lastPlacedIds.has(fid)) continue
			const mem = this.labelPositionMemory.get(fid)
			if (mem === undefined || mem >= positionOffsets.length) continue
			const size = labelSizeByImportance[c.node.importanceTier]
			const halfW = size.w / 2
			const pad = LocationsDatasetLayer.LABEL_SPACING_PADDING_PX
			const [dx, dy] = positionOffsets[mem]
			incumbentBoxes.set(fid, {
				x1: c.x + dx - halfW - pad,
				y1: c.y + dy - pad,
				x2: c.x + dx + halfW + pad,
				y2: c.y + dy + size.h + pad
			})
		}

		// Try to place a label at a specific position index
		// Returns the placement info or null if position is blocked
		const tryPositionAt = (
			c: typeof pooled[0],
			posIndex: number,
			maxConflict: number,
			selfId: string
		): { box: LabelBox, screenX: number, screenY: number, conflict: number, softConflict: number } | null => {
			const size = labelSizeByImportance[c.node.importanceTier]
			const halfW = size.w / 2
			const pad = LocationsDatasetLayer.LABEL_SPACING_PADDING_PX
			const [dx, dy] = positionOffsets[posIndex]

			const labelX = c.x + dx
			const labelY = c.y + dy

			const box: LabelBox = {
				x1: labelX - halfW - pad,
				y1: labelY - pad,
				x2: labelX + halfW + pad,
				y2: labelY + size.h + pad
			}

			if (!isOnScreen(box)) return null

			let totalConflict = 0
			for (const placed of placedBoxes) {
				const area = overlapArea(box, placed)
				if (area > 0) {
					totalConflict += area
				}
			}

			if (totalConflict > maxConflict) return null

			// Soft conflict: overlap with displayed labels that haven't been
			// placed yet this pass. Not blocking, but steers new labels away
			// from spots that would displace an existing label
			let softConflict = 0
			for (const [id, ib] of incumbentBoxes) {
				if (id === selfId) continue
				softConflict += overlapArea(box, ib)
			}

			return { box, screenX: labelX, screenY: labelY + size.h / 2, conflict: totalConflict, softConflict }
		}

		// Choose a position for a label that is NOT currently displayed,
		// preferring its remembered offset (recently faded labels reappear in
		// place), otherwise the least-conflicted position counting both hard
		// (placed) and soft (existing incumbent) overlap
		const tryPlaceLabel = (
			c: typeof pooled[0],
			preferredPosIndex: number | undefined,
			selfId: string
		): { box: LabelBox, screenX: number, screenY: number, posIndex: number } | null => {
			const maxConflict = 150

			// Remembered position wins outright when it is clean
			if (preferredPosIndex !== undefined && preferredPosIndex < positionOffsets.length) {
				const result = tryPositionAt(c, preferredPosIndex, maxConflict, selfId)
				if (result?.conflict === 0 && result.softConflict === 0) {
					return { ...result, posIndex: preferredPosIndex }
				}
			}

			// Try all positions, find the least-conflicted one
			let bestResult: { box: LabelBox, screenX: number, screenY: number, posIndex: number, total: number } | null = null

			for (let i = 0; i < positionOffsets.length; i++) {
				const result = tryPositionAt(c, i, maxConflict, selfId)
				if (result) {
					const total = result.conflict + result.softConflict
					if (bestResult === null || total < bestResult.total) {
						bestResult = { box: result.box, screenX: result.screenX, screenY: result.screenY, posIndex: i, total }
					}
					// If completely clean, use immediately
					if (total === 0) break
				}
			}

			return bestResult
		}

		// Candidates are already spatially well distributed: rank-based selection
		// guarantees each region's dominant cities are present, and the rank-first
		// score ordering places them before any region's secondary cities.
		const allCandidates = pooled

		// Labels still present in the overlay (including mid-fade) are
		// position-locked along with last pass's winners: a label that missed
		// one pass but is still visible may only be re-placed at its
		// remembered offset. It must fully fade out before it can reappear at
		// a different offset - otherwise a contested label ping-pongs between
		// two positions at the placement cadence while fully opaque.
		const activeOverlayIds = this.labelsOverlay.getActiveCityIds()

		// Track which cities we successfully placed this frame
		const placedCityIds = new Set<string>()

		// Place labels in priority order
		for (const c of allCandidates) {
			if (visibleLabels.length >= maxLabels) break
			if (c.node === focusedNode) continue

			const featureId = String(c.node.feature.id)
			const preferredPos = this.labelPositionMemory.get(featureId)
			const isIncumbent = preferredPos !== undefined && preferredPos < positionOffsets.length &&
				(this.lastPlacedIds.has(featureId) || activeOverlayIds.has(featureId))

			// A displayed label's position is immutable: incumbents only ever
			// try their existing offset (with tolerance for transient grazing
			// overlaps) and fade out when genuinely blocked - they never
			// relocate to make room for or yield to another label.
			let placement: { box: LabelBox, screenX: number, screenY: number, posIndex: number } | null
			if (isIncumbent) {
				const kept = tryPositionAt(c, preferredPos, this.INCUMBENT_MAX_CONFLICT_PX2, featureId)
				placement = kept
					? { box: kept.box, screenX: kept.screenX, screenY: kept.screenY, posIndex: preferredPos }
					: null
			} else {
				placement = tryPlaceLabel(c, preferredPos, featureId)
			}
			if (!placement) continue

			// This label's footprint is now a hard box for everyone after it
			incumbentBoxes.delete(featureId)
			placedBoxes.push(placement.box)

			// Remember this position for next frame (stability)
			this.labelPositionMemory.set(featureId, placement.posIndex)
			placedCityIds.add(featureId)

			// Get weather data from grid cache if available
			const weather = this.buildWeatherInfo(c.node, this.lastShowWind)

			visibleLabels.push({
				id: featureId,
				name: c.node.name,
				importance: c.node.importanceTier,
				screenX: placement.screenX,
				screenY: placement.screenY,
				cityScreenX: c.x,
				cityScreenY: c.y,
				alpha: c.alpha,
				worldDepth: c.worldDepth,
				shouldShow: true,
				weather
			})
			visibleIds.add(featureId)
		}

		// Provide position updates for active labels that weren't placed this
		// pass (fading out, or held by hide hysteresis). They keep their
		// remembered offset and weather so nothing about them visually
		// changes except the eventual fade.
		for (const cityId of activeOverlayIds) {
			// Skip if already in visible list
			if (visibleIds.has(cityId)) continue

			// Find the city node using the feature ID (same key as sceneObjects map)
			const sceneObject = this.citySceneObjects.get(cityId)
			if (!sceneObject) continue
			if (sceneObject === focusedNode) continue

			// Compute screen position for the fading label
			const pos = sceneObject.cartesianPosition
			const screen = state.camera.worldToScreen(pos)

			// Only include if screen position is valid
			if (!Number.isFinite(screen.x) || !Number.isFinite(screen.y)) continue

			// Add with shouldShow=false to indicate it's fading out but needs position update
			const labelPos = this.rememberedLabelPosition(cityId, sceneObject.importanceTier, screen.x, screen.y)
			visibleLabels.push({
				id: cityId,
				name: sceneObject.name,
				importance: sceneObject.importanceTier,
				screenX: labelPos.x,
				screenY: labelPos.y,
				cityScreenX: screen.x,
				cityScreenY: screen.y,
				// 0 once the city is edge-on/behind the limb: the overlay
				// skips the hide hysteresis for that geometric hide
				alpha: LocationsDatasetLayer.grazingAlpha(LocationsDatasetLayer.cityIncidence(cameraPos, pos)),
				worldDepth: this.cityWorldDepth(state, sceneObject),
				shouldShow: false,
				weather: this.buildWeatherInfo(sceneObject, this.lastShowWind)
			})
		}

		// Update last seen time for placed cities
		for (const cityId of placedCityIds) {
			this.labelLastSeenTime.set(cityId, now)
		}

		// Clean up position memory - but persist for a while after removal
		// This allows labels to smoothly reappear in the same position
		for (const cityId of this.labelPositionMemory.keys()) {
			if (!placedCityIds.has(cityId)) {
				const lastSeen = this.labelLastSeenTime.get(cityId) ?? 0
				if (now - lastSeen > this.POSITION_MEMORY_PERSIST_MS) {
					this.labelPositionMemory.delete(cityId)
					this.labelLastSeenTime.delete(cityId)
				}
			}
		}

		// Remember this pass's selection so reprojection frames keep fading
		// labels fading instead of resurrecting them
		this.lastPlacedIds = visibleIds

		// Update the overlay with all labels (visible + fading). Motion state
		// selects cause-aware timing: long hysteresis while panning, short
		// while zooming so the set tracks altitude promptly.
		this.labelsOverlay.updateLabels(visibleLabels, this.cameraMotion)
	}

	/**
	 * Frames between placement passes: reproject the current label set
	 * (including ones mid-fade) without re-running collision. Labels the
	 * last pass faded out stay faded (lastPlacedIds gate) so this never
	 * fights the declutter decisions made at most PLACEMENT_INTERVAL_MS ago.
	 */
	private updatePositionsOnly(state: RenderState, focusedNode: FocusTarget | undefined): void {
		const activeCityIds = this.labelsOverlay.getActiveCityIds()
		const cameraPos = state.camera.position
		const cameraAltitudeKm = new geographic(cameraPos).altitude
		const width = state.width
		const height = state.height
		const margin = 300
		const visibleLabels: CityLabelData[] = []

		for (const cityId of activeCityIds) {
			const sceneObject = this.citySceneObjects.get(cityId)
			if (!sceneObject) continue
			if (sceneObject === focusedNode) continue

			const pos = sceneObject.cartesianPosition
			// Same opacity the placement pass would assign, so the grazing
			// and altitude fades track the camera every frame, not just at
			// the placement cadence
			const alpha = LocationsDatasetLayer.altitudeAlpha(cameraAltitudeKm, sceneObject.importanceTier)
				* LocationsDatasetLayer.grazingAlpha(LocationsDatasetLayer.cityIncidence(cameraPos, pos))

			const screen = state.camera.worldToScreen(pos)
			if (!Number.isFinite(screen.x) || !Number.isFinite(screen.y)) continue

			// Apply the remembered label offset so position doesn't jump
			const posIndex = this.labelPositionMemory.get(cityId)
			const [dx, dy] = posIndex !== undefined && posIndex < LocationsDatasetLayer.POSITION_OFFSETS.length
				? LocationsDatasetLayer.POSITION_OFFSETS[posIndex]
				: LocationsDatasetLayer.POSITION_OFFSETS[0]
			const size = LocationsDatasetLayer.LABEL_SIZE_BY_IMPORTANCE[sceneObject.importanceTier]
			const labelX = screen.x + dx
			const labelY = screen.y + dy + size.h / 2

			const onScreen = labelX > -margin && labelX < width + margin &&
				labelY > -margin && labelY < height + margin
			const shouldShow = alpha > 0 && onScreen && this.lastPlacedIds.has(cityId)

			// Preserve weather data from cache
			const weather = this.buildWeatherInfo(sceneObject, this.lastShowWind)

			visibleLabels.push({
				id: cityId,
				name: sceneObject.name,
				importance: sceneObject.importanceTier,
				screenX: labelX,
				screenY: labelY,
				cityScreenX: screen.x,
				cityScreenY: screen.y,
				alpha,
				worldDepth: this.cityWorldDepth(state, sceneObject),
				shouldShow,
				weather
			})
		}

		this.labelsOverlay.updateLabels(visibleLabels, this.cameraMotion)
	}


	// ==================== Query Methods ====================

	/**
	 * Query cities by importance tier
	 */
	public getCitiesByImportance(importance: CityImportance): CityLabelNode[] {
		const cities: CityLabelNode[] = []

		for (const sceneObject of this.citySceneObjects.values()) {
			if (sceneObject.feature.properties.importance === importance) {
				cities.push(sceneObject)
			}
		}

		return cities
	}

	/**
	 * Query cities within a bounding box
	 */
	public getCitiesInBounds(
		minLon: number,
		minLat: number,
		maxLon: number,
		maxLat: number
	): CityLabelNode[] {
		const cities: CityLabelNode[] = []

		for (const sceneObject of this.citySceneObjects.values()) {
			const geom = sceneObject.feature.geometry
			const coords = geom?.type === 'Point' ? geom.coordinates : undefined
			if (coords && coords.length >= 2) {
				const [lon, lat] = coords
				if (lon >= minLon && lon <= maxLon && lat >= minLat && lat <= maxLat) {
					cities.push(sceneObject)
				}
			}
		}

		return cities
	}

	/**
	 * Get statistics about loaded cities
	 */
	public getStatistics(): {
		total: number
		byImportance: Record<CityImportance, number>
	} {
		const stats = {
			total: this.citySceneObjects.size,
			byImportance: {
				capital: 0,
				major: 0,
				medium: 0,
				small: 0,
				town: 0
			} as Record<CityImportance, number>
		}

		for (const sceneObject of this.citySceneObjects.values()) {
			const importance = sceneObject.feature.properties.importance
			if (importance in stats.byImportance) {
				stats.byImportance[importance]++
			}
		}

		return stats
	}

	// ==================== Lifecycle ====================

	/**
	 * Clean up overlay when layer is disposed
	 */
	override dispose(): void {
		if (this.overlayInitialized) {
			this.context.selectionCoordinator.removeLabelNode(this.labelsOverlay)
			this.labelsOverlay.destroy()
		}

		// Clean up city scene objects
		for (const [id] of this.citySceneObjects) {
			this.removeCitySceneObject(id)
		}

		super.dispose()
	}
}
